// setColumnGenerated — DdlEdit builder + commit dispatcher for the
// computed / generated-column surface (DSE2 §3.A3).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice A3.
//
// One op fans into this module:
//   • add-column with `column.generated?: GeneratedSpec` — the generated spec
//     (expression text + persistence flag) wired into a CREATE TABLE source
//     column-list insert or an ALTER TABLE ADD COLUMN clause. DSE2 §3.A3
//     synthesizers translate to the dialect's idiom (mssql `AS (expr)
//     [PERSISTED]`, pgsql `GENERATED ALWAYS AS (expr) STORED`, mysql/sqlite
//     `GENERATED ALWAYS AS (expr) [VIRTUAL | STORED]`, oracle `GENERATED
//     ALWAYS AS (expr) [VIRTUAL]`).
//
// `alter-column` does not yet carry a `generated` field on `ColumnChange`
// in DSE2 §3.A1's union; consumers wanting to swap a generated column's
// expression today do a drop + add round-trip on the buffer (or wait for a
// later DSE2 widening). This module exposes a documented predicate
// (`isAlterColumnGeneratedSupported`) so the form can disable the affordance
// instead of building a doomed edit.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  ColumnDef,
  DdlEdit,
  GeneratedSpec,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

// ─── add-column with generated ────────────────────────────────────────────

export interface AddColumnWithGeneratedArgs {
  readonly column: ColumnDef;
  readonly afterColumn?: string;
}

export function buildAddColumnWithGeneratedEdit(args: AddColumnWithGeneratedArgs): DdlEdit {
  return {
    op: 'add-column',
    column: args.column,
    afterColumn: args.afterColumn,
  };
}

export async function commitAddColumnWithGenerated(
  ctx: VqeDdlEditContext,
  args: AddColumnWithGeneratedArgs,
): Promise<void> {
  const edit = buildAddColumnWithGeneratedEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

// ─── per-engine persistence-mode gating ───────────────────────────────────

/** Form-level persistence mode (`'stored'` or `'virtual'`). Maps to
 *  `GeneratedSpec.virtual`: `'stored'` → `virtual: false`, `'virtual'` →
 *  `virtual: true`. The form uses this enum so the radio buttons read
 *  consistently across engines even though synthesizers spell the keywords
 *  differently (mssql PERSISTED ↔ no keyword, pgsql/mysql/sqlite
 *  STORED ↔ VIRTUAL, oracle no keyword ↔ VIRTUAL). */
export type GeneratedPersistenceMode = 'stored' | 'virtual';

/** True when the engine accepts the supplied persistence mode for column-level
 *  GENERATED. The form gates the radio with this predicate before the user
 *  submits (vs surfacing a runtime synthesizer error). */
export function isGeneratedPersistenceSupported(
  engine: SqlEngine,
  mode: GeneratedPersistenceMode,
): boolean {
  if (engine === 'postgres') return mode === 'stored';
  if (engine === 'oracle') return mode === 'virtual';
  return true;
}

/** Per-engine tooltip explaining why a persistence mode is disabled. The form
 *  surfaces this on the disabled radio. The literal text mirrors DSE2 §3.A3's
 *  per-engine notes so the UI explanation stays in sync with the synthesizer's
 *  rejection message. */
export function getGeneratedPersistenceTooltip(
  engine: SqlEngine,
  mode: GeneratedPersistenceMode,
): string {
  if (engine === 'postgres' && mode === 'virtual') {
    return 'PostgreSQL only supports STORED generated columns (no VIRTUAL).';
  }
  if (engine === 'oracle' && mode === 'stored') {
    return 'Oracle column-level GENERATED is virtual-only — no STORED keyword.';
  }
  return '';
}

// ─── per-engine notes (sqlite version + form hints) ───────────────────────

/** Engine-version requirement note (best-effort, not a runtime version probe).
 *  sqlite 3.31.0+ is the minimum for column-level GENERATED — older engines
 *  reject the syntax outright. The form surfaces this string as a hint when
 *  the engine is sqlite. Other engines return an empty string. */
export function getGeneratedEngineVersionNote(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'Generated columns require SQLite 3.31.0 or newer.';
  }
  return '';
}

/** True when the form should enable an `alter-column → set generated` path.
 *  v2 §3.A3 only ships add-column with `GeneratedSpec`; ColumnChange does not
 *  yet carry a `generated` field. The form consults this predicate (currently
 *  `false` for every engine) and surfaces the documented redirect tooltip
 *  instead of attempting a doomed commit. The `engine` parameter is unused
 *  today — it exists so future widening (per-engine `alter-column → SET
 *  GENERATED` support) can land without changing call sites. */
export function isAlterColumnGeneratedSupported(engine: SqlEngine): boolean {
  void engine;
  return false;
}

/** Documented redirect tooltip for the alter-column generated path. Points
 *  consumers at the drop + add workaround that works today across every
 *  supported engine. The `engine` parameter mirrors
 *  `isAlterColumnGeneratedSupported` so call sites can pass the engine without
 *  branching on the predicate's return shape. */
export function getAlterColumnGeneratedUnsupportedTooltip(engine: SqlEngine): string {
  void engine;
  return (
    'Generated-column expressions cannot be modified in place. '
      + 'Drop the column and add it back with the new expression.'
  );
}

// ─── per-engine validation for GeneratedSpec on add-column ────────────────

/** Pre-emit validation mirroring DSE2's per-engine `formatGeneratedSpec` rules.
 *  Returns null when the spec is acceptable for the engine; returns a human
 *  message when the synthesizer would throw. The form uses this to disable
 *  Submit before the user attempts a doomed commit. */
export function validateGeneratedSpecForEngine(
  engine: SqlEngine,
  spec: GeneratedSpec,
): string | null {
  if (spec.expr.trim() === '') {
    return 'Generated columns require an expression.';
  }
  if (engine === 'postgres' && spec.virtual === true) {
    return 'PostgreSQL only supports STORED generated columns (no VIRTUAL).';
  }
  if (engine === 'oracle' && spec.virtual === false) {
    return 'Oracle column-level GENERATED is virtual-only — no STORED keyword.';
  }
  return null;
}

/** True when the supplied `GeneratedSpec` is non-empty — i.e. an expression is
 *  provided. The bare-expr `{ expr: '' }` shape is treated as "user has not
 *  chosen anything yet" and Submit stays disabled. */
export function isNonEmptyGeneratedSpec(spec: GeneratedSpec): boolean {
  return spec.expr.trim() !== '';
}

/** Map the form-level persistence mode to `GeneratedSpec.virtual`. Returns
 *  `undefined` when the form should let the engine pick the default — this
 *  matches the DSE2 synthesizers' behaviour when `virtual` is left unset. */
export function persistenceModeToVirtual(
  mode: GeneratedPersistenceMode,
): boolean {
  return mode === 'virtual';
}
